import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import apiService from '../services/apiService'
import { BookOpen, Trash2, Check } from 'lucide-react'
import ConfirmModal from '../ui/ConfirmModal'

// Custom StatusBadge component
const StatusBadge = ({ status }) => {
  if (status === 'None' || !status) {
    return (
      <div className="flex items-center justify-center w-full">
        <div className="bg-[rgba(233,0,4,0.10)] flex items-center justify-center gap-1 px-2 py-1.5 rounded-[20px]">
          <span className="flex items-center">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="7"
                cy="7"
                r="6.5"
                fill="#fff"
                stroke="#E90004"
                strokeWidth="1"
              />
              <path
                d="M4.5 4.5L9.5 9.5M9.5 4.5L4.5 9.5"
                stroke="#E90004"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <div className="text-xs text-[#E90004] font-medium">Not Assigned</div>
        </div>
      </div>
    )
  }
  return (
    <div className="flex items-center justify-center w-full">
      <div className="bg-[rgba(4,146,185,0.1)] flex items-center justify-center gap-1 px-2 py-1.5 rounded-[20px]">
        <Check className="w-3.5 h-3.5 text-[rgba(4,146,185,1)]" />
        <div className="text-xs text-[rgba(4,146,185,1)] font-medium">Assigned</div>
      </div>
    </div>
  )
}

function StudentCourseList() {
  const [data, setData] = useState([])
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const { id } = useParams()
  const navigate = useNavigate()
  const [unassignModalOpen, setUnassignModalOpen] = useState(false)
  const [courseToUnassign, setCourseToUnassign] = useState(null)
  const api = apiService()

  useEffect(() => {
    if (id) {
      getCourses()
    }
  }, [id])

  const getCourses = async () => {
    try {
      setLoading(true)
      const response = await api.get(`courses/student/${id}`)
      if (response.status === 200) {
        setData(response.data.courses || [])
      }
    } catch (error) {
      console.error('Error fetching courses:', error)
      toast.error('Failed to fetch courses')
    } finally {
      setLoading(false)
    }
  }

  const unassignCourse = async (courseId) => {
    try {
      const response = await api.del(`courses/${courseId}/student/${id}`)
      if (response.status === 200) {
        toast.success('Course unassigned successfully')
        await getCourses()
        setUnassignModalOpen(false)
        setCourseToUnassign(null)
      }
    } catch (error) {
      console.error('Error unassigning course:', error)
      toast.error('Failed to unassign course')
    }
  }

  const checkCourse = (courseId) => {
    let index = courses.indexOf(courseId)
    let coursesTemp = courses.slice()
    if (index > -1) {
      coursesTemp.splice(index, 1)
    } else {
      coursesTemp.push(courseId)
    }
    setCourses(coursesTemp)
  }

  const handleSubmitAssign = async (e) => {
    e.preventDefault()
    if (courses.length === 0) {
      toast.error('Please select at least one course')
    } else {
      try {
        const response = await api.post('courses/assign', {
          student_id: id,
          courses: courses,
        })
        if (response.status === 201) {
          toast.success('Courses assigned successfully')
          setCourses([])
          await getCourses()
        }
      } catch (error) {
        console.error('Error assigning courses:', error)
        toast.error(error.response?.data?.message || 'Failed to assign courses')
      }
    }
  }

  const handleUnassignClick = (courseId) => {
    setCourseToUnassign(courseId)
    setUnassignModalOpen(true)
  }

  const handleConfirmUnassign = () => {
    if (courseToUnassign) {
      unassignCourse(courseToUnassign)
    }
  }

  // Map data to reference structure for easier rendering
  const mappedData = data.map((item, index) => ({
    id: item._id,
    sNo: String(index + 1).padStart(2, '0'),
    courseName: item.title,
    description: item.description || 'No description',
    status: item.assigned ? 'Assigned' : 'None',
    isActive: item.assigned,
    isSelected: item.assigned || courses.includes(item._id),
    thumbnail: item.thumbnail,
    raw: item,
  }))

  if (loading) {
    return (
      <div className="w-full mt-2.5 max-md:max-w-full">
        <div className="bg-white border border-[#F0F0F0] rounded-[10px] p-8">
          <div className="text-center">Loading courses...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full mt-2.5 max-md:max-w-full">
      <div className="justify-between items-center border border-[color:var(--Stock,#F0F0F0)] flex w-full gap-[40px_100px] font-medium leading-[1.2] flex-wrap bg-white px-5 py-3.5 rounded-[10px_10px_0px_0px] border-solid max-md:max-w-full">
        <div className="text-[#030303] text-lg self-stretch my-auto">
          Courses List
        </div>
        <button
          className="justify-center items-stretch flex flex-col text-xs text-white text-center w-[120px] bg-[#DE3C7D] my-auto p-2.5 rounded-md hover:bg-[#c72c6a] transition-colors font-medium"
          type="submit"
          onClick={handleSubmitAssign}
        >
          <div className="flex w-full gap-1 justify-center items-center">
            <span>Assign Courses</span>
          </div>
        </button>
      </div>

      {/* Table Header */}
      <div className="flex w-full bg-neutral-50 border-b border-l border-r border-[#F0F0F0] max-md:max-w-full">
        <div className="justify-center border-r-[color:var(--Stock,#F0F0F0)] flex flex-col text-xs text-[#535862] font-medium whitespace-nowrap text-center leading-none w-[80px] px-5 py-2 border-r border-solid">
          <div className="flex gap-1 justify-center">
            <span>Select</span>
          </div>
        </div>
        <div className="justify-center border-r-[color:var(--Stock,#F0F0F0)] flex flex-col text-xs text-[#535862] font-medium whitespace-nowrap text-center leading-none w-[100px] px-5 py-2 border-r border-solid">
          <div className="flex gap-1 justify-center">
            <span>S.No</span>
          </div>
        </div>
        <div className="justify-center border-r-[color:var(--Stock,#F0F0F0)] flex flex-col text-xs text-[#535862] font-medium whitespace-nowrap text-center leading-none flex-1 px-5 py-2 border-r border-solid">
          <div className="flex gap-1 justify-center">
            <span>Course Name</span>
          </div>
        </div>
        <div className="justify-center border-r-[color:var(--Stock,#F0F0F0)] flex flex-col text-xs text-[#535862] font-medium whitespace-nowrap text-center leading-none w-[199px] px-5 py-2 border-r border-solid">
          <div className="flex gap-1 justify-center">
            <span>Status</span>
          </div>
        </div>
        <div className="justify-center flex flex-col text-xs text-[#535862] font-medium whitespace-nowrap text-center leading-none w-[150px] px-5 py-2">
          <div className="flex gap-1 justify-center">
            <span>Actions</span>
          </div>
        </div>
      </div>

      {/* Table Body */}
      {mappedData.length === 0 ? (
        <div className="bg-white border-b border-l border-r border-[#F0F0F0] p-8 text-center">
          <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-[#535862]">No courses available</p>
        </div>
      ) : (
        mappedData.map((item, index) => (
          <div
            key={item.id}
            className={`flex w-full border-b border-l border-r border-[#F0F0F0] max-md:max-w-full ${
              index % 2 === 0 ? 'bg-white' : 'bg-neutral-50'
            }`}
          >
            <div className="justify-center border-r-[color:var(--Stock,#F0F0F0)] flex flex-col text-xs text-[#030303] font-medium whitespace-nowrap text-center leading-none w-[80px] px-5 py-2 border-r border-solid">
              <input
                type="checkbox"
                checked={item.isSelected}
                onChange={() => checkCourse(item.id)}
                className="w-4 h-4 text-[#DE3C7D] border-gray-300 rounded focus:ring-[#DE3C7D] cursor-pointer"
              />
            </div>
            <div className="justify-center border-r-[color:var(--Stock,#F0F0F0)] flex flex-col text-xs text-[#030303] font-medium whitespace-nowrap text-center leading-none w-[100px] px-5 py-2 border-r border-solid">
              {item.sNo}
            </div>
            <div className="justify-center border-r-[color:var(--Stock,#F0F0F0)] flex flex-col text-xs text-[#030303] font-medium whitespace-nowrap text-center leading-none flex-1 px-5 py-2 border-r border-solid">
              <div className="flex items-center gap-3">
                {item.thumbnail && (
                  <img
                    src={item.thumbnail.startsWith('/uploads/') ? `${process.env.REACT_APP_BASE_URL?.replace(/\/$/, '')}${item.thumbnail}` : item.thumbnail}
                    alt={item.courseName}
                    className="w-12 h-12 object-cover rounded"
                    onError={(e) => {
                      e.target.style.display = 'none'
                    }}
                  />
                )}
                {!item.thumbnail && (
                  <div className="w-12 h-12 bg-gradient-to-br from-[#DE3C7D] to-[#c8356f] rounded flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-white" />
                  </div>
                )}
                <div className="text-left">
                  <div className="font-semibold">{item.courseName}</div>
                  <div className="text-[#535862] text-xs line-clamp-1">{item.description}</div>
                </div>
              </div>
            </div>
            <div className="justify-center border-r-[color:var(--Stock,#F0F0F0)] flex flex-col text-xs text-[#030303] font-medium whitespace-nowrap text-center leading-none w-[199px] px-5 py-2 border-r border-solid">
              <StatusBadge status={item.status} />
            </div>
            <div className="justify-center flex flex-col text-xs text-[#030303] font-medium whitespace-nowrap text-center leading-none w-[150px] px-5 py-2">
              {item.isActive && (
                <button
                  onClick={() => handleUnassignClick(item.id)}
                  className="flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors mx-auto"
                  title="Unassign course"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Unassign</span>
                </button>
              )}
            </div>
          </div>
        ))
      )}

      <ConfirmModal
        isOpen={unassignModalOpen}
        onClose={() => {
          setUnassignModalOpen(false)
          setCourseToUnassign(null)
        }}
        onConfirm={handleConfirmUnassign}
        title="Unassign Course"
        message="Are you sure you want to unassign this course from the student? This action cannot be undone."
      />
    </div>
  )
}

export default StudentCourseList

